import { useCallback, useLayoutEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { getCategories } from '../common/category'
import { Child, getChild } from '../common/child'
import { useErrorHandle } from '../common/error/errorHandler'
import { showLoading, notifyMessage } from '../common/store/slices/application'
import { memberUrl } from '../common/constant/appUrl'
import { OperationId } from '../common/constant/operationLog'
import { yesNo } from '../common/constant/classification'
import { useOperationLog } from '../common/operationLog'
import { tabValue } from '../member/memberService'
import { GetCategoryMstDto } from '../../dataAccess/webApi/dto/categoryMstDto'
import { blankToNull } from '../../utils/stringUtil'
import { translate } from '../../i18n'
import { getAddressStringByPostalCode } from '../common/location'
import { executePutChild } from '../../dataAccess/webApi/dao/childrenDao'

/** カテゴリーキー */
const CATEGORY_KEYS = ['childrenAddress']

/** 市民用事業ID（市民用の項目は事業設ID'0'） */
const DEFAULT_PROJECT_ID = '0'

/** URLパラメータ */
interface UrlParams {
  childId: string
}

/** お子さま住所・電話番号の入力フォームの項目の定義 */
export interface Inputs {
  sameAddressFlag?: string | null
  tel?: string | null
  postalCode?: string | null
  address1?: string | null
  address2?: string | null
  buildingNameRoomNumber?: string | null
}

export const useAction = () => {
  const errorHandle = useErrorHandle()
  const history = useHistory()
  const dispatch = useDispatch()

  // URLパラメータ取得
  const { childId } = useParams<UrlParams>()
  // カテゴリーマスタ
  const [categoryMst, setCategoryMst] = useState<GetCategoryMstDto[]>()
  // お子さま情報
  // const [child, setChild] = useState<GetChildDto>()
  // お子さま詳細更新日時
  const [updateDatetime, setUpdateDatetime] = useState<string>()
  // // リザルトコード
  const [childrenResultCode, setChildrenResultCode] = useState<number>()
  // 未入力エラー
  const [noInputError, setNoInputError] = useState<string>()
  // オペレーションログ
  const { addOperationLog } = useOperationLog()

  // 入力フォーム初期値設定
  const formMethods = useForm<Inputs>({
    defaultValues: {}
  })

  // 初期処理（ちらつき防止のためにuseLayoutEffect使用）
  useLayoutEffect(() => {
    addOperationLog({
      operationId: OperationId.OP_00000001,
      accessData: [{ userIdRegFlag: yesNo.yes, childId }]
    })
    
    dispatch(
      showLoading(
        errorHandle(async () => {
          const [
            category, 
            child,
          ] = await Promise.all([
            // カテゴリーマスタ取得
            getCategories({projectId: DEFAULT_PROJECT_ID, categories: CATEGORY_KEYS}),
            // お子さま情報取得
            getChild(childId),
          ])
          // カテゴリーマスタ設定
          setCategoryMst(category)
          // お子さま情報設定
          setUpdateDatetime(child.updateDatetime)
          // 入力フォームに設定
          formMethods.reset({
            ...formMethods.getValues(),
            ...getInitialInputs(child),
          })
        })
      )
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 確定処理
  const onSubmit = useCallback(
    (data: Inputs) => {
      addOperationLog({ operationId: OperationId.OP_00000037 })

      // 全ての項目が未入力かどうかをチェック
      const { sameAddressFlag, tel, postalCode, address1, address2, buildingNameRoomNumber } = formMethods.getValues();
      if (sameAddressFlag !== yesNo.yes && tel === '' && postalCode === '' && address1 === '' && address2 === '' && buildingNameRoomNumber === '') {
        // 未入力の場合、エラーメッセージを表示
        setNoInputError(translate('memberModifyChildAddress.error.noInputError'))
      } else {
        dispatch(
          showLoading({
            process: errorHandle(async () => {
              // 登録実行
              if (updateDatetime){
                const resp = await putChild(data, childId, updateDatetime)
                
              
                if (resp.resultCode) {
                  // 失敗時
                  setChildrenResultCode(resp.resultCode)
                } else {
                  // 成功時
                  dispatch(notifyMessage(translate('memberModifyChildAddress.success.completionMessage')))
                  history.push(memberUrl.url(), { tabValue: tabValue.child, childId })
                }
              }
            }),
            isHiddenMain: false,
          })
        )
      }
    },
    [addOperationLog, updateDatetime]
  )
  
  //保護者と同じチェックの有無
  const sameAddressFlag = useWatch({ name: 'sameAddressFlag', control: formMethods.control })
  
  const autoCompleteAddress = useCallback(async () => {
    const { postalCode } = formMethods.getValues()
    
    if (postalCode && postalCode.length > 0) {
      try {
        const address = await getAddressStringByPostalCode(postalCode);
        if (address) {
          formMethods.setValue('address1', address)
        }
      } catch (error) {
        //console.error("郵便番号からの住所取得中にエラーが発生しました:", error)
      }
    } else {
      //console.warn("郵便番号が入力されていません。")
    }
  }, [])
  
  return {
    CATEGORY_KEYS,
    formMethods,
    categoryMst,
    onSubmit,
    childrenResultCode,
    sameAddressFlag,
    autoCompleteAddress,
    noInputError
  }
}

/**
 * 入力フォームの初期値設定
 */
const getInitialInputs = (child: Child) => {
   return {
    sameAddressFlag: child.sameAddressFlag !== yesNo.yes ? '' : child.sameAddressFlag,
    tel: child.tel ?? '',
    postalCode: child.postalCode ?? '',
    address1: child.address1 ?? '',
    address2: child.address2 ?? '',
    buildingNameRoomNumber: child.buildingNameRoomNumber ?? '',
  }
}

const putChild = (data: Inputs, childId: string, updateDatetime: string) => {
  return executePutChild(childId, {
    sameAddressFlag: data.sameAddressFlag ? data.sameAddressFlag : yesNo.no,
    tel: blankToNull(data.tel),
    postalCode: blankToNull(data.postalCode),
    address1: blankToNull(data.address1),
    address2: blankToNull(data.address2),
    buildingNameRoomNumber: blankToNull(data.buildingNameRoomNumber),

    updateDatetime,
  })
}